import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Input, Alert, Select } from '../components/design-system';
import { Save, Settings, Brain, Clock, Shield, Zap, Pause, Play, Trash2 } from 'lucide-react';

export const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [housekeepingRunning, setHousekeepingRunning] = useState(false);
  const [housekeepingStats, setHousekeepingStats] = useState(null);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await api.getSettings();
      const flat = {};
      for (const [key, val] of Object.entries(res.settings || {})) {
        flat[key] = typeof val.value === 'string' ? val.value : JSON.stringify(val.value);
      }
      setSettings(flat);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const parsed = {};
      for (const [key, val] of Object.entries(settings)) {
        try { parsed[key] = JSON.parse(val); }
        catch { parsed[key] = val; }
      }
      await api.updateSettings(parsed);
      setSuccess('Settings saved successfully');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const set = (key) => (e) => setSettings({ ...settings, [key]: e.target.value });

  const isTasksPaused = settings.tasks_paused === 'true' || settings.tasks_paused === true;

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <p className="text-sm text-gray-500">Configure AI generation, thresholds, and system behavior</p>
        </div>
        {isAdmin && <Button icon={Save} loading={saving} onClick={handleSave}>Save All</Button>}
      </div>

      {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Task Scheduling */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          {isTasksPaused ? (
            <Pause className="h-4 w-4 text-amber-500" />
          ) : (
            <Play className="h-4 w-4 text-green-500" />
          )}
          <h3 className="text-base font-semibold text-gray-900">Task Scheduling</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Pause Scheduled Tasks</p>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, the scheduled cron tasks will be skipped. The worker continues to trigger, but tasks will exit early without processing.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isTasksPaused}
                onChange={(e) => setSettings({ ...settings, tasks_paused: e.target.checked ? 'true' : 'false' })}
                disabled={!isAdmin}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 peer-disabled:opacity-50"></div>
            </label>
          </div>
          {isTasksPaused && (
            <Alert type="warning" onClose={() => {}}>
              Scheduled tasks are currently paused. News sources will not be fetched, items will not be analyzed, and articles will not be generated until this is re-enabled.
            </Alert>
          )}
        </div>
      </Card>

      {/* AI Generation */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Brain className="h-4 w-4 text-primary-500" />
          <h3 className="text-base font-semibold text-gray-900">AI Generation</h3>
        </div>
        <div className="space-y-4">
          <Input label="AI Model" value={settings.ai_model || ''} onChange={set('ai_model')} disabled={!isAdmin}
            placeholder='@cf/meta/llama-3.1-8b-instruct' />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Sources for Generation" type="number" min="1" value={settings.min_sources_for_generation || '2'}
              onChange={set('min_sources_for_generation')} disabled={!isAdmin} />
            <Input label="Max Article Length (words)" type="number" min="100" value={settings.max_article_length || '2000'}
              onChange={set('max_article_length')} disabled={!isAdmin} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Importance Threshold (0-1)" type="number" min="0" max="1" step="0.05"
              value={settings.importance_threshold || '0.3'} onChange={set('importance_threshold')} disabled={!isAdmin} />
            <Input label="Neutrality Target (0-1)" type="number" min="0" max="1" step="0.05"
              value={settings.neutrality_target || '0.85'} onChange={set('neutrality_target')} disabled={!isAdmin} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="autoPublish" checked={settings.auto_publish === 'true' || settings.auto_publish === true}
              onChange={(e) => setSettings({ ...settings, auto_publish: e.target.checked ? 'true' : 'false' })}
              disabled={!isAdmin} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <label htmlFor="autoPublish" className="text-sm text-gray-700">Auto-publish approved articles</label>
          </div>
        </div>
      </Card>

      {/* Clustering */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-4 w-4 text-amber-500" />
          <h3 className="text-base font-semibold text-gray-900">Clustering & Analysis</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Cluster Similarity Threshold" type="number" min="0" max="1" step="0.05"
              value={settings.cluster_similarity_threshold || '0.20'} onChange={set('cluster_similarity_threshold')} disabled={!isAdmin} />
            <Input label="Duplicate Threshold" type="number" min="0" max="1" step="0.05"
              value={settings.duplicate_threshold || '0.9'} onChange={set('duplicate_threshold')} disabled={!isAdmin} />
          </div>
        </div>
      </Card>

      {/* Source Fetching */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-blue-500" />
          <h3 className="text-base font-semibold text-gray-900">Source Fetching</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Default Fetch Interval (min)" type="number" min="5"
              value={settings.default_fetch_interval || '15'} onChange={set('default_fetch_interval')} disabled={!isAdmin} />
            <Input label="Max Concurrent Fetches" type="number" min="1"
              value={settings.max_concurrent_fetches || '5'} onChange={set('max_concurrent_fetches')} disabled={!isAdmin} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="socialMonitoring"
              checked={settings.enable_social_monitoring === 'true' || settings.enable_social_monitoring === true}
              onChange={(e) => setSettings({ ...settings, enable_social_monitoring: e.target.checked ? 'true' : 'false' })}
              disabled={!isAdmin} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <label htmlFor="socialMonitoring" className="text-sm text-gray-700">Enable social media monitoring</label>
          </div>
        </div>
      </Card>

      {/* Housekeeping */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Trash2 className="h-4 w-4 text-red-500" />
          <h3 className="text-base font-semibold text-gray-900">Housekeeping</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Automatically removes outdated content to save resources. Runs every 15 minutes as part of the scheduled pipeline.
          Published and approved articles are always kept.
        </p>
        <div className="space-y-4">
          <Input label="Retention Days" type="number" min="1" max="90"
            value={settings.retention_days || '7'} onChange={set('retention_days')} disabled={!isAdmin}
            helperText="News items, stale clusters, unpublished articles, and logs older than this will be deleted" />
          {isAdmin && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Run Housekeeping Now</p>
                  <p className="text-xs text-gray-500 mt-0.5">Immediately clean up outdated content based on current retention setting</p>
                </div>
                <Button icon={Trash2} variant="secondary" size="sm" loading={housekeepingRunning}
                  onClick={async () => {
                    setHousekeepingRunning(true); setError(''); setHousekeepingStats(null);
                    try {
                      const res = await api.runHousekeeping();
                      setHousekeepingStats(res.stats);
                      setSuccess(`Housekeeping complete: ${res.stats.totalDeleted} items removed`);
                    } catch (err) { setError(err.message); }
                    finally { setHousekeepingRunning(false); }
                  }}>
                  Run Now
                </Button>
              </div>
              {housekeepingStats && (
                <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ['News Items', housekeepingStats.newsItemsDeleted],
                    ['Orphaned Items', housekeepingStats.orphanedUsedItemsDeleted],
                    ['Clusters (stale)', housekeepingStats.clustersMarkedStale],
                    ['Clusters (deleted)', housekeepingStats.clustersDeleted],
                    ['Unpub. Articles', housekeepingStats.unpublishedArticlesDeleted],
                    ['Jobs', housekeepingStats.jobsDeleted],
                    ['API Logs', housekeepingStats.apiLogsDeleted],
                    ['Activity Logs', housekeepingStats.activityLogsDeleted],
                  ].map(([label, count]) => (
                    <div key={label} className="text-center">
                      <div className="text-lg font-bold text-gray-900">{count || 0}</div>
                      <div className="text-[10px] text-gray-500">{label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Language */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-4 w-4 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">General</h3>
        </div>
        <Input label="Default Language" value={settings.default_language || 'en'} onChange={set('default_language')} disabled={!isAdmin} />
      </Card>

      {isAdmin && (
        <div className="flex justify-end">
          <Button icon={Save} loading={saving} onClick={handleSave}>Save All Settings</Button>
        </div>
      )}
    </div>
  );
};
